import {
    CaseOrderStatus,
    DocStatus,
    TOnSignFileData,
} from 'components/DocBox/DocBox.types';
import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
} from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useSchema } from 'domain/ClientService';
import { useGetCustomService } from 'hooks/rest/useGetCustomService';
import { useGenericAPIMutation } from 'hooks/mutations/useGenericAPIMutation';
import { getBase64FromFile } from 'utils/file';
import ServiceManager from 'services/ServiceManager';
import useQueryParams from 'hooks/helpers/useQueryParams';
import { dateFormatZurich } from 'utils/datetime';
import { useManagerProfile } from 'prodivers/managerProfile';
import { CaseDetailsContextProps } from './CaseDetailsManager.types';
import { adaptCaseDetailsData } from './adapter';

const CaseDetailsContext = createContext<CaseDetailsContextProps | undefined>(
    undefined,
);

export const CaseDetailsReducer = (state, action) => {
    switch (action.type) {
        case 'SAVE_CASE_DATA':
            return { ...state, ...action.payload };
        case 'UPDATE_DOC_SIGN_STATUS':
            return {
                ...state,
                status: action?.payload?.status,
                submitted: action?.payload?.submitted,
                submittedBy: action?.payload?.submittedBy,
                isSignatureVerified: action?.payload?.isSignatureVerified,
                documents: state?.documents?.map((doc) => {
                    if (doc.documentId === action?.payload?.documentId) {
                        return {
                            ...doc,
                            status: action?.payload?.docStatus,
                        };
                    }

                    return doc;
                }),
            };
        default:
            return state;
    }
};

const CaseDetailsManager = ({ children, submittedCase = false }) => {
    const {
        t,
        i18n: { language },
    } = useTranslation();
    const { caseId, dfsClientId } = useParams();
    const { getParam } = useQueryParams();
    const { search } = useLocation();
    const { data } = useManagerProfile();
    const navigate = useNavigate();
    const { dataLists } = useSchema();

    const reasonForSaleOptions = useMemo(
        () => dataLists
            ?.find((item) => item.listKey === 'reasonForSale')?.listItems?.map((itm) => ({
                value: `${itm?.id}`,
                label: itm?.label,
            })),
        [dataLists],
    ) || [];

    const containerId = getParam('containerId') as string;
    const externalId = getParam('externalId') as string;

    const [state, dispatch] = React.useReducer(CaseDetailsReducer, {});

    const {
        data: caseDetails,
        isLoading: caseDetailsDataLoading,
        error: caseDetailsError,
    } = useGetCustomService({
        service: 'customCaseManagementServices',
        api: 'getCaseById',
        payload: { caseId },
        enabled: !submittedCase && !!caseId,
    });

    const {
        data: submittedCaseDetails,
        isLoading: submittedCaseDetailsDataLoading,
        error: submittedCaseDetailsError,
    } = useGetCustomService({
        service: 'customCaseManagementServices',
        api: 'getSubmittedCaseById',
        payload: { externalId },
        enabled: !!submittedCase && !!externalId,
    });

    const {
        data: caseDocuments,
        isLoading: caseDocumentsLoading,
    } = useGetCustomService({
        service: 'customCaseManagementServices',
        api: 'getDocumentsByCaseId',
        payload: { caseId, language },
        enabled: !!caseId,
    });

    const {
        data: portfolioDetails,
        isLoading: portfolioDetailsLoading,
    } = useGetCustomService({
        service: 'customInvestmentService',
        api: 'getLastApplicationByPortfolio',
        payload: {
            portfolioId:
                caseDetails?.data?.portfolioId
                || submittedCaseDetails?.data?.externalDetails?.portfolioId,
            language,
        },
        enabled: !!caseDetails?.data?.portfolioId || !!submittedCaseDetails?.data?.externalDetails?.portfolioId,
    });

    const {
        data: planDetails,
        isLoading: planDetailsLoading,
    } = useGetCustomService({
        service: 'planService',
        api: 'getPlanDetails',
        payload: {
            portfolioId:
                caseDetails?.data?.portfolioId
                || submittedCaseDetails?.data?.externalDetails?.portfolioId,
        },
        enabled: !!caseDetails?.data?.portfolioId || !!submittedCaseDetails?.data?.externalDetails?.portfolioId,
    });

    const {
        mutateAsync: handleCaseOrderMutations,
        isPending: caseOrderMutationOnLoading,
    } = useGenericAPIMutation();

    const cancelCase = useCallback(async () => {
        await handleCaseOrderMutations({
            service: 'customCaseManagementServices',
            API: 'deleteCase',
            payload: {
                caseId,
            },
            invalidateQueryKeys: ['getData_getCasesByPortfolioId',
                'getData_getSubmittedCaseById',
                'getData_getCaseById'],
        });
        dispatch({
            type: 'UPDATE_DOC_SIGN_STATUS',
            payload: {
                status: CaseOrderStatus.Cancelled,
                docStatus: DocStatus.Canceled,
            },
        });
    }, [caseId]);

    const submitCase = useCallback(async () => {
        await handleCaseOrderMutations({
            service: 'customCaseManagementServices',
            API: 'submitCaseOrder',
            payload: {
                caseId,
            },
            invalidateQueryKeys: [
                'getData_getCasesByPortfolioId',
                'getData_getSubmittedCaseById',
                'getData_getCaseById',
            ],
        });
        dispatch({
            type: 'UPDATE_DOC_SIGN_STATUS',
            payload: {
                status: CaseOrderStatus.Submitted,
                docStatus: DocStatus.ReadyToReUpload,
                submitted: moment().format(dateFormatZurich),
                submittedBy: data?.name || '-',
                isSignatureVerified: true,
            },
        });
    }, [caseId, data?.name]);

    const onManualSign = async ({ file, documentId }: TOnSignFileData) => {
        const base64File = await getBase64FromFile(file as File);
        const splitted = base64File.split(',');
        const payload = {
            file: splitted[1],
            documentId,
            isNewSigningFlow: true,
        };

        await handleCaseOrderMutations({
            service: 'customDocumentsService',
            API: 'manualDocumentSign',
            payload,
            invalidateQueryKeys: ['getData_getDocumentsByCaseId', 'getData_getCaseById'],
        });

        // await manualDocumentSign(payload);
        dispatch({
            type: 'UPDATE_DOC_SIGN_STATUS',
            payload: {
                documentId,
                status: CaseOrderStatus.ReadyToSubmit,
                docStatus: DocStatus.ReadyToSubmit,
            },
        });
    };

    const onESign = useCallback(
        async ({ documentId }: { documentId: number }) => {
            const response = await ServiceManager.customDocumentsService(
                'getESignUrl',
                [
                    {
                        documentId,
                        returnUrl: `${window.location.origin}${window.location.pathname}${search}`,
                        language,
                    },
                ],
            ) as any;

            window.open(response.data?.signingUrl, '_self');
        },
        [dfsClientId, search, language],
    );

    const handleDocReUpload = useCallback(
        async (documentId: number) => {
            await handleCaseOrderMutations({
                service: 'customCaseManagementServices',
                API: 'generateCaseDocument',
                payload: {
                    caseId,
                    language,
                },
                invalidateQueryKeys: ['getData_getDocumentsByCaseId', 'getData_getCaseById'],
            });

            dispatch({
                type: 'UPDATE_DOC_SIGN_STATUS',
                payload: {
                    documentId,
                    status: CaseOrderStatus.ReadyForSigning,
                    docStatus: DocStatus.ReadyToSign,
                    isSignatureVerified: false,
                },
            });
        },
        [caseId, language],
    );

    const handleVerifySignature = useCallback(async () => {
        await handleCaseOrderMutations({
            service: 'customCaseManagementServices',
            API: 'verifySignature',
            payload: {
                caseId,
            },
            invalidateQueryKeys: ['getData_getCaseById'],
        });

        dispatch({
            type: 'SAVE_CASE_DATA',
            payload: {
                isSignatureVerified: true,
            },
        });
    }, [caseId, language]);

    useEffect(() => {
        const caseDetailsData = adaptCaseDetailsData({
            caseData:
                caseDetails?.data || submittedCaseDetails?.data?.caseDetails,
            externalCaseData: submittedCaseDetails?.data?.externalDetails,
            portfolioData: portfolioDetails?.data,
            planData: planDetails?.data,
            documents: caseDocuments?.data?.documents,
            language,
            submittedCase,
            reasonForSaleOptions,
            fromDL: submittedCase && !submittedCaseDetails?.data?.caseDetails,
            t,
        });

        dispatch({
            type: 'SAVE_CASE_DATA',
            payload: { ...caseDetailsData },
        });
    }, [
        caseDetails?.data,
        portfolioDetails?.data,
        planDetails?.data,
        caseDocuments?.data?.documents,
        language,
        submittedCase,
        submittedCaseDetails?.data,
        reasonForSaleOptions,
    ]);

    useEffect(() => {
        if (
            [
                caseDetailsError?.status,
                submittedCaseDetailsError?.status,
            ]?.includes(404)
        ) {
            navigate('/404');
        }
    }, [caseDetailsError?.status, submittedCaseDetailsError?.status]);

    const caseStatus = state?.status;

    const isAllDocReadyToSubmit = useMemo(
        () => !state?.documents?.some(
            (doc) => doc?.status !== DocStatus.ReadyToSubmit,
        ),
        [state?.documents],
    );
    const isCaseReadyForSubmission = useMemo(
        () => caseStatus === CaseOrderStatus.ReadyToSubmit
            || isAllDocReadyToSubmit,
        [caseStatus, isAllDocReadyToSubmit],
    );

    const isCaseCancelable = useMemo(
        () => ![CaseOrderStatus.Cancelled, CaseOrderStatus.Submitted].includes(
            caseStatus,
        ),
        [caseStatus],
    );

    const cachedState = useMemo(() => ({
        ...state,
        dispatch,
        isLoading:
                    caseDetailsDataLoading
                    || planDetailsLoading
                    || portfolioDetailsLoading
                    || submittedCaseDetailsDataLoading,
        caseOrderMutationOnLoading:
                    caseDocumentsLoading || caseOrderMutationOnLoading,
        cancelCase,
        isCaseCancelable,
        submitCase,
        isCaseReadyForSubmission,
        onManualSign,
        onESign,
        handleDocReUpload,
        handleVerifySignature,
        containerId,
        dfsClientId,
        portfolioId:
                    caseDetails?.data?.portfolioId
                    || submittedCaseDetails?.data?.externalDetails?.portfolioId,
        isAllDocReadyToSubmit,
    }), [
        state, dispatch,
        cancelCase,
        isCaseCancelable,
        submitCase,
        isCaseReadyForSubmission,
        onManualSign,
        onESign,
        handleDocReUpload,
        handleVerifySignature,
        containerId,
        dfsClientId,
        caseDetails?.data?.portfolioId,
        submittedCaseDetails?.data?.externalDetails?.portfolioId,
        isAllDocReadyToSubmit,
        caseDetailsDataLoading,
        planDetailsLoading,
        portfolioDetailsLoading,
        submittedCaseDetailsDataLoading,
        caseDocumentsLoading,
        caseOrderMutationOnLoading,
    ]);

    return (
        <CaseDetailsContext.Provider
            value={cachedState}
        >
            {children}
        </CaseDetailsContext.Provider>
    );
};

export default CaseDetailsManager;

export const useCaseDetails = () => {
    // Logic to fetch case details goes here
    // This could be a call to an API or a context provider

    const context = useContext(CaseDetailsContext);

    if (!context) {
        throw new Error(
            'useCaseDetails must be used within a CaseDetailsManager provider!',
        );
    }

    return context;
};
